/* calendar-view.jsx — the drop-in <CalendarView> component.
 *
 * Self-contained calendar feature: toolbar (Month/Week/Agenda + period nav),
 * status legend filter, optional project-scope selector, and full meeting CRUD
 * via the right-side drawer. All UI lives here — the host only supplies data and
 * two persistence callbacks.
 *
 * PROPS
 *   meetings            Array<Meeting>   — the meetings to render (see README for shape)
 *   onSave(meeting)     fn               — called on create AND update; persist, then
 *                                          feed the updated array back in via `meetings`
 *   onDelete(id)        fn               — called on delete
 *   scopeProjectId      string|null      — lock the calendar to one project (project-tab
 *                                          mode). When set, the scope <select> is hidden
 *                                          and new meetings prefill this project.
 *   defaultView         "month"|"week"|"agenda"   — initial view (default "month")
 *   showScopeSelector   bool             — show the All-projects/scope dropdown (default true;
 *                                          ignored / forced false when scopeProjectId is set)
 *   title               string           — header title (default "Calendar")
 *   onToast(msg)        fn               — optional; if omitted an internal toast is shown
 *
 * DEPENDENCIES (from the host app)
 *   - CAL  ........ data adapter + date helpers (calendar-data.js) — point at your API
 *   - Icon ........ icons.jsx (needs the calendar/video/users/today paths — see README)
 *   - Avatar, Pill, Segmented ... ui.jsx primitives
 *   - MonthView/WeekView/AgendaView ... calendar-views.jsx
 *   - MeetingDrawer ... calendar-drawer.jsx
 *   - calendar.css ... styles + status color tokens
 */
const { useState: cvUseState, useMemo: cvUseMemo, useEffect: cvUseEffect } = React;

function cvPeriodLabel(view, anchor) {
  if (view === "week") {
    const ws = CAL.startOfWeek(anchor), we = CAL.addDays(ws, 6);
    return { main: `${CAL.fmtDayShort(ws)} – ${CAL.fmtDayShort(we)}`, yr: ws.slice(0, 4) };
  }
  return { main: CAL.MONTHS[Number(anchor.slice(5, 7)) - 1], yr: anchor.slice(0, 4) };
}
function cvStepAnchor(view, anchor, dir) {
  if (view === "week") return CAL.addDays(anchor, dir * 7);
  const d = CAL.fromIso(anchor.slice(0, 8) + "01"); d.setMonth(d.getMonth() + dir); return CAL.toIso(d);
}

function CalendarView({
  meetings,
  onSave,
  onDelete,
  scopeProjectId = null,
  currentEmployeeId = null,
  defaultView = "month",
  showScopeSelector = true,
  title = "Calendar",
  onToast,
  onError,
  emailReady = false,
  sendingMeetingId = "",
  onSendLink,
  onIngestTranscript,
  showHeader = false,
}) {
  const [view, setView] = cvUseState(defaultView);
  const [anchor, setAnchor] = cvUseState(CAL.todayIso());
  const [scope, setScope] = cvUseState(scopeProjectId || "all");
  const [attendeeScope, setAttendeeScope] = cvUseState("all");
  const [statusOff, setStatusOff] = cvUseState({});
  const [drawer, setDrawer] = cvUseState(null);     // { mode:'new'|'edit', meeting }
  const [toast, setToast] = cvUseState(null);

  cvUseEffect(() => { setScope(scopeProjectId || "all"); }, [scopeProjectId]);
  cvUseEffect(() => { if (!currentEmployeeId) setAttendeeScope("all"); }, [currentEmployeeId]);
  cvUseEffect(() => { if (!toast) return; const id = setTimeout(() => setToast(null), 2000); return () => clearTimeout(id); }, [toast]);

  const flash = (m) => { if (onToast) onToast(m); else setToast(m); };
  const locked = !!scopeProjectId;
  const effectiveScope = locked ? scopeProjectId : scope;
  const scopeProject = effectiveScope !== "all" ? CAL.projectById[effectiveScope] : null;

  const scoped = cvUseMemo(
    () => effectiveScope === "all" ? meetings : meetings.filter((m) => m.projectId === effectiveScope),
    [meetings, effectiveScope]
  );
  const attendeeScoped = cvUseMemo(() => {
    if (attendeeScope !== "mine" || !currentEmployeeId) return scoped;
    const employeeId = String(currentEmployeeId).toLowerCase();
    return scoped.filter((m) => m.kind === "milestone" || (m.attendees || []).some((a) => a.type === "employee" && String(a.id).toLowerCase() === employeeId));
  }, [scoped, attendeeScope, currentEmployeeId]);
  const visible = cvUseMemo(() => attendeeScoped.filter((m) => !statusOff[m.status]), [attendeeScoped, statusOff]);
  const statusCounts = cvUseMemo(
    () => Object.fromEntries(CAL.MEETING_STATUSES.map((status) => [status, attendeeScoped.filter((m) => m.status === status).length])),
    [attendeeScoped]
  );

  // ---- CRUD ----
  const openCreate = (date, start, end) => setDrawer({ mode: "new", meeting: CAL.newMeeting({
    date: date || CAL.todayIso(), start: start || "10:00", end: end || "11:00",
    projectId: scopeProject ? scopeProject.id : "",
  }) });
  const openEdit = (m) => {
    if (m.kind === "milestone") return;
    setDrawer({ mode: "edit", meeting: { ...m, attendees: m.attendees.map((a) => ({ ...a })) } });
  };
  const closeDrawer = () => setDrawer(null);

  const handleSave = async (m) => {
    const isNew = drawer && drawer.mode === "new";
    try {
      await onSave(m);
      flash(isNew ? "Meeting created" : "Meeting updated");
      closeDrawer();
    } catch (e) {
      if (onError) onError(e);
    }
  };
  const handleDelete = async (id) => {
    const m = meetings.find((x) => x.id === id);
    if (!window.confirm(`Delete “${m ? m.title : "this meeting"}”?`)) return;
    try {
      await onDelete(id);
      flash("Meeting deleted");
      closeDrawer();
    } catch (e) {
      if (onError) onError(e);
    }
  };
  const handleSendLink = async (m) => {
    if (!onSendLink) return;
    try {
      const result = await onSendLink(m);
      const recipientCount = result && result.recipientCount;
      flash(`Meeting link sent${Number.isFinite(recipientCount) ? ` to ${recipientCount} attendee${recipientCount === 1 ? "" : "s"}` : ""}`);
    } catch (e) {
      if (onError) onError(e);
    }
  };
  const handleIngestTranscript = async (m, employee) => {
    if (!onIngestTranscript) return;
    try {
      await onIngestTranscript(m, employee);
      flash(`Transcript sent to ${employee.name}`);
    } catch (e) {
      if (onError) onError(e);
      throw e;
    }
  };

  const toggleStatus = (s) => setStatusOff((cur) => ({ ...cur, [s]: !cur[s] }));
  const label = cvPeriodLabel(view, anchor);

  return (
    <React.Fragment>
      {showHeader && (
        <header className="topbar">
          <div>
            <div className="crumb">{scopeProject ? `Projects · ${scopeProject.name}` : "Operations"}</div>
            <h1>{title}</h1>
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setAnchor(CAL.todayIso())}><Icon name="today" size={14} />Today</button>
          <button className="btn" onClick={() => openCreate(CAL.todayIso())}><Icon name="plus" size={14} />New meeting</button>
        </header>
      )}

      <div className={showHeader ? "content" : "cal-content"}>
        {locked && scopeProject && (
          <div className="panel" style={{ marginBottom: 12, padding: "9px 14px", display: "flex", alignItems: "center", gap: 10, background: "var(--primary-tint)" }}>
            <Icon name="projects" size={15} style={{ color: "var(--primary)" }} />
            <span style={{ fontSize: 12.5 }}>Calendar scoped to <strong>{scopeProject.name}</strong>.</span>
          </div>
        )}

        <div className="cal-toolbar">
          <Segmented options={[{ value: "month", label: "Month" }, { value: "week", label: "Week" }, { value: "agenda", label: "Agenda" }]} value={view} onChange={setView} />
          <div className="cal-nav">
            <button title="Previous" onClick={() => setAnchor(cvStepAnchor(view, anchor, -1))}><Icon name="chevL" size={15} /></button>
            <button title="Next" onClick={() => setAnchor(cvStepAnchor(view, anchor, 1))}><Icon name="chevR" size={15} /></button>
          </div>
          <span className="cal-period">{label.main} <span className="yr">{label.yr}</span></span>
          {!showHeader && <button className="btn ghost sm" onClick={() => setAnchor(CAL.todayIso())}><Icon name="today" size={13} />Today</button>}
          {!showHeader && <button className="btn sm" onClick={() => openCreate(CAL.todayIso())}><Icon name="plus" size={13} />New meeting</button>}

          <div className="cal-spacer" />

          {currentEmployeeId && (
            <div className="cal-attendee-filter">
              <span className="lbl">Attendee</span>
              <Segmented
                options={[{ value: "all", label: "All" }, { value: "mine", label: "Me" }]}
                value={attendeeScope}
                onChange={setAttendeeScope}
              />
            </div>
          )}

          <div className="cal-legend">
            {[["unprocessed", "sw-unprocessed"], ["Scheduled", "sw-sched"], ["Done", "sw-done"], ["Cancelled", "sw-cancel"]].map(([s, sw]) => (
              <button key={s} className={statusOff[s] ? "off" : ""} onClick={() => toggleStatus(s)} title={`Toggle ${s}`}>
                <span className={`sw ${sw}`} />{s === "unprocessed" ? "Unprocessed" : s === "Done" ? "Completed" : s}
                <span className="muted" style={{ fontFamily: "var(--mono)", marginLeft: 2 }}>{statusCounts[s]}</span>
              </button>
            ))}
          </div>

          {showScopeSelector && !locked && (
            <div className="cal-scope">
              <span className="lbl">Scope</span>
              <select value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="all">All projects</option>
                {CAL.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Icon name="chevD" size={13} style={{ color: "var(--faint)" }} />
            </div>
          )}
        </div>

        {view === "month" && <MonthView meetings={visible} anchorIso={anchor} onOpen={openEdit} onCreate={openCreate} onPickDay={(iso) => { setAnchor(iso); setView("week"); }} />}
        {view === "week" && <WeekView meetings={visible} anchorIso={anchor} onOpen={openEdit} onCreate={openCreate} />}
        {view === "agenda" && <AgendaView meetings={visible} anchorIso={anchor} onOpen={openEdit} />}
      </div>

      {drawer && <MeetingDrawer meeting={drawer.meeting} isNew={drawer.mode === "new"} scopeProjectId={scopeProjectId} emailReady={emailReady} sendingLink={sendingMeetingId === drawer.meeting.id} onClose={closeDrawer} onSave={handleSave} onDelete={handleDelete} onSendLink={handleSendLink} onIngestTranscript={handleIngestTranscript} />}
      {!onToast && toast && <div className="toast"><Icon name="check" size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />{toast}</div>}
    </React.Fragment>
  );
}

function cleanCalendarResults(meeting) {
  return (meeting.results || []).filter((result) => (result.text || "").trim());
}

function calendarResultChanged(before, after) {
  return (
    (before.type || "Action") !== (after.type || "Action") ||
    (before.text || "").trim() !== (after.text || "").trim()
  );
}

async function syncCalendarResults(eventId, beforeMeeting, afterMeeting, scopeProjectId) {
  let saved = null;
  const beforeResults = cleanCalendarResults(beforeMeeting || { results: [] });
  const afterResults = cleanCalendarResults(afterMeeting);
  const afterIds = new Set(afterResults.filter((result) => result.id).map((result) => result.id));
  const beforeById = Object.fromEntries(beforeResults.filter((result) => result.id).map((result) => [result.id, result]));

  for (const result of beforeResults) {
    if (result.id && !afterIds.has(result.id)) {
      saved = await window.API.deleteCalendarEventFollowup(eventId, result.id, scopeProjectId);
    }
  }

  for (const result of afterResults) {
    if (result.id && beforeById[result.id]) {
      if (calendarResultChanged(beforeById[result.id], result)) {
        saved = await window.API.updateCalendarEventFollowup(eventId, result.id, result, scopeProjectId);
      }
    } else {
      saved = await window.API.createCalendarEventFollowup(eventId, result, scopeProjectId);
    }
  }

  return saved;
}

async function saveCalendarMeeting(meeting, events, scopeProjectId) {
  const body = CAL.toEventBody(meeting, scopeProjectId, { includeResults: false });
  const isNew = !meeting.id || String(meeting.id).startsWith("m");
  const existingEvent = events.find((event) => event.id === meeting.id);
  const beforeMeeting = existingEvent ? CAL.toMeeting(existingEvent) : null;
  let saved = isNew
    ? await window.API.createCalendarEvent(body, scopeProjectId)
    : await window.API.updateCalendarEvent(meeting.id, body, scopeProjectId);
  saved = await syncCalendarResults(saved.id, beforeMeeting, meeting, scopeProjectId) || saved;
  return saved;
}

function calendarTranscriptIngestPrompt(meeting, savedEvent, project, db) {
  const attendeeLines = (meeting.attendees || []).map((attendee) => `- ${CAL.attendeeLabel(attendee)} (${CAL.attendeeMeta(attendee) || attendee.type})`);
  const followupLines = (meeting.results || [])
    .filter((result) => (result.text || "").trim())
    .map((result) => `- ${result.type || "Remark"}: ${String(result.text || "").trim()}`);
  const customers = (project && project.customerIds || [])
    .map((customerId) => (db.customers || []).find((customer) => customer.id === customerId))
    .filter(Boolean)
    .map((customer) => customer.name)
    .join(", ");

  return [
    "You are ingesting a meeting transcript for a linked Appligator project.",
    "",
    "Instructions:",
    "- Review the meeting details and the full transcript below.",
    "- Find all takeaways from the transcript that may be important to the linked project, including decisions, requirements, risks, blockers, action items, open questions, scope changes, customer expectations, deadlines, constraints, and technical details.",
    "- Add exactly one follow-up to this calendar meeting containing the consolidated project-relevant takeaways.",
    "- Use the Appligator meeting follow-up tool for the calendar meeting, with the meeting id and linked project id shown below.",
    "- Use type \"Remark\" unless one single different follow-up type is clearly more appropriate.",
    "- Keep the follow-up concise but complete; short grouped bullets are fine.",
    "- Do not create tickets, project knowledge, documents, notes, or multiple follow-ups unless the user asks later.",
    "- If the transcript contains no project-relevant takeaways, still add one Remark follow-up stating that no project-relevant takeaways were found.",
    "",
    "Linked project:",
    `- Project: ${(project && project.name) || "Unknown project"}`,
    `- Project ID: ${meeting.projectId || savedEvent.projectId || ""}`,
    customers ? `- Customer: ${customers}` : "- Customer: not linked",
    project && project.status ? `- Status: ${project.status}` : "",
    project && project.kind ? `- Kind: ${project.kind}` : "",
    "",
    "Calendar meeting:",
    `- Meeting ID: ${savedEvent.id}`,
    `- Title: ${meeting.title || savedEvent.title || "Untitled meeting"}`,
    `- Type: ${meeting.type || savedEvent.type || "Call"}`,
    `- Status: ${meeting.status || savedEvent.status || "Scheduled"}`,
    `- Date: ${meeting.date || ""}`,
    `- Time: ${meeting.start || ""}-${meeting.end || ""}`,
    meeting.videoLink ? `- Video link: ${meeting.videoLink}` : "",
    "",
    "Attendees:",
    attendeeLines.length ? attendeeLines.join("\n") : "- None recorded",
    "",
    "Agenda / notes:",
    (meeting.agenda || "").trim() || "(none)",
    "",
    "Existing follow-ups:",
    followupLines.length ? followupLines.join("\n") : "- None recorded",
    "",
    "Transcript:",
    (meeting.transcript || "").trim(),
  ].filter((line) => line !== "").join("\n");
}

function calendarTranscriptIngestReference(meeting, savedEvent, project, db) {
  const customers = (project && project.customerIds || [])
    .map((customerId) => (db.customers || []).find((customer) => customer.id === customerId))
    .filter(Boolean)
    .map((customer) => customer.name)
    .join(", ");
  return {
    type: "Meeting",
    id: savedEvent.id,
    label: meeting.title || savedEvent.title || "Meeting transcript",
    url: `#calendar/${encodeURIComponent(savedEvent.id)}`,
    fields: {
      ProjectId: meeting.projectId || savedEvent.projectId || "",
      Project: (project && project.name) || "",
      Customer: customers,
      MeetingId: savedEvent.id,
      MeetingType: meeting.type || savedEvent.type || "Call",
      MeetingStatus: meeting.status || savedEvent.status || "Scheduled",
      MeetingDate: meeting.date || "",
      Transcript: (meeting.transcript || "").trim(),
    },
  };
}

function CalendarPane({ db, scopeProjectId = null, currentEmployeeId = null, title = "Calendar", defaultView = "month", showScopeSelector = true, onToast, onError }) {
  CAL.setDb(db);
  const [events, setEvents] = cvUseState(() => (db.calendarEvents || []).filter((event) => !scopeProjectId || event.projectId === scopeProjectId));
  const [loading, setLoading] = cvUseState(false);
  const [sendingMeetingId, setSendingMeetingId] = cvUseState("");

  cvUseEffect(() => { CAL.setDb(db); }, [db]);
  cvUseEffect(() => {
    let cancelled = false;
    setLoading(true);
    window.API.listCalendarEvents(scopeProjectId ? { projectId: scopeProjectId } : {})
      .then((rows) => { if (!cancelled) setEvents(rows || []); })
      .catch((e) => { if (!cancelled && onError) onError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [db, scopeProjectId]);

  const meetings = cvUseMemo(
    () => [...events.map(CAL.toMeeting), ...CAL.milestonesForCalendar(db, scopeProjectId)],
    [events, db, scopeProjectId]
  );
  const upsert = (saved) => setEvents((cur) => {
    const exists = cur.some((event) => event.id === saved.id);
    return exists ? cur.map((event) => event.id === saved.id ? saved : event) : [...cur, saved];
  });

  const saveMeeting = async (meeting) => {
    const saved = await saveCalendarMeeting(meeting, events, scopeProjectId);
    upsert(saved);
    return saved;
  };
  const deleteMeeting = async (id) => {
    await window.API.deleteCalendarEvent(id, scopeProjectId);
    setEvents((cur) => cur.filter((event) => event.id !== id));
  };
  const sendMeetingLink = async (meeting) => {
    if (sendingMeetingId) return null;
    setSendingMeetingId(meeting.id);
    try {
      const saved = await saveCalendarMeeting(meeting, events, scopeProjectId);
      upsert(saved);
      return await window.API.sendCalendarMeetingLink(saved.id, scopeProjectId || saved.projectId || null);
    } finally {
      setSendingMeetingId("");
    }
  };
  const ingestTranscript = async (meeting, employee) => {
    const saved = await saveCalendarMeeting(meeting, events, scopeProjectId);
    upsert(saved);
    const projectId = meeting.projectId || saved.projectId || scopeProjectId || "";
    const project = (db.projects || []).find((item) => item.id === projectId) || CAL.projectById[projectId] || null;
    const prompt = calendarTranscriptIngestPrompt({ ...meeting, projectId }, saved, project, db);
    const reference = calendarTranscriptIngestReference({ ...meeting, projectId }, saved, project, db);
    await window.API.startActionCenterContextTask(employee.id, prompt, reference);
  };

  return (
    <div className="cal-pane">
      {loading && <div className="cal-loading">Loading calendar...</div>}
      <CalendarView
        meetings={meetings}
        onSave={saveMeeting}
        onDelete={deleteMeeting}
        scopeProjectId={scopeProjectId}
        currentEmployeeId={currentEmployeeId}
        defaultView={defaultView}
        showScopeSelector={showScopeSelector}
        title={title}
        onToast={onToast}
        onError={onError}
        emailReady={!!(db.settings && db.settings.emailEnabled && db.settings.emailBackendConfigured && db.settings.emailSmtpHost && db.settings.emailSmtpPort)}
        sendingMeetingId={sendingMeetingId}
        onSendLink={sendMeetingLink}
        onIngestTranscript={ingestTranscript}
      />
    </div>
  );
}

Object.assign(window, { CalendarView, CalendarPane, saveCalendarMeeting });
